import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Divider,
  Fade,
} from "@mui/material";
import { Loader } from "../../components/CustomLoader";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";
import { capitalize } from "../../utils/helpers";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activity-tabpanel-${index}`}
      aria-labelledby={`activity-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserActivityTracking = ({ userId, show, onHide, data }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [bidHistory, setBidHistory] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [savedLots, setSavedLots] = useState([]);


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!show) return null;

  return (
    <Fade in={true} timeout={300}>
      <Box>
        {/* Activity Tracking Header */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ 
            background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", 
            p: 4,
            position: "relative"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, color: "white" }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>📊</Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                  User Activity Tracking
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  {data ? `${data.firstName} ${data.lastName} - ${data.email}` : "Loading user data..."}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Activity Tabs */}
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <Box sx={{ 
            backgroundColor: '#f8f9fa',
            borderBottom: 1, 
            borderColor: 'divider',
            px: 3,
            py: 1
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="activity tabs"
              className="orange-tabs"
            >
              <Tab label="Login History" />
              <Tab label="Bid History" />
              <Tab label="Order History" />
              <Tab label="Saved Lots" />
            </Tabs>
          </Box>
          
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Loader />
            </Box>
          ) : (
            <CardContent sx={{ p: 0 }}>

              <TabPanel value={tabValue} index={0}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" className="orange-section-title" sx={{ mb: 3 }}>
                    Login History
                  </Typography>
                  <TableContainer 
                    component={Paper}
                    sx={{
                      maxHeight: loginHistory.length > 0 ? 400 : 'auto',
                      overflow: loginHistory.length > 0 ? 'auto' : 'hidden',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Table>
                      <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Date & Time</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>IP Address</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Device</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                       
                            <TableRow 
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: '#fef3c7',
                                  transition: 'background-color 0.2s ease'
                                }
                              }}
                            >
                              <TableCell>{new Date(data.lastLogin).toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={data.ipAddress} 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: '#F59E0B',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </TableCell>
                              <TableCell>{data.deviceType}</TableCell>
                            </TableRow>
                         
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>Bid History</Typography>
            <TableContainer 
              component={Paper}
              sx={{
                maxHeight: bidHistory.length > 0 ? 400 : 'auto',
                overflow: bidHistory.length > 0 ? 'auto' : 'hidden'
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Auction</TableCell>
                    <TableCell>Bid Amount</TableCell>
                    <TableCell>Bid Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bidHistory.length > 0 ? (
                    bidHistory.map((bid, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {bid.auctionTitle}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Lot #{bid.lotNumber}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>${bid.bidAmount?.toLocaleString()}</TableCell>
                        <TableCell>{new Date(bid.bidTime).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={bid.status}
                            color={bid.status === "active" ? "success" : bid.status === "outbid" ? "warning" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={bid.result || "Pending"}
                            color={bid.result === "won" ? "success" : bid.result === "lost" ? "error" : "default"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No bid history found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>Order History</Typography>
            <TableContainer 
              component={Paper}
              sx={{
                maxHeight: orderHistory.length > 0 ? 400 : 'auto',
                overflow: orderHistory.length > 0 ? 'auto' : 'hidden'
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderHistory.length > 0 ? (
                    orderHistory.map((order, index) => (
                      <TableRow key={index}>
                        <TableCell>#{order.orderId}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                              src={order.itemImage ? IMAGE_BASE + order.itemImage : "/path/to/default.jpg"}
                              alt={order.itemName}
                              sx={{ width: 40, height: 40 }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {order.itemName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.auctionTitle}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>${order.totalAmount?.toLocaleString()}</TableCell>
                        <TableCell>{new Date(order.orderDate).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            color={order.status === "completed" ? "success" : order.status === "pending" ? "warning" : "error"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No order history found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>Saved Lots / Watchlist</Typography>
            <Grid container spacing={2}>
              {savedLots.length > 0 ? (
                savedLots.map((lot, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <Box
                        component="img"
                        src={lot.image ? IMAGE_BASE + lot.image : "/path/to/default.jpg"}
                        alt={lot.title}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                        }}
                      />
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {lot.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Lot #{lot.lotNumber}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Current Bid: ${lot.currentBid?.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Saved: {new Date(lot.savedAt).toLocaleDateString()}
                        </Typography>
                        <Box mt={1}>
                          <Chip
                            label={lot.auctionStatus}
                            color={lot.auctionStatus === "active" ? "success" : "default"}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box textAlign="center" py={4}>
                    <Typography color="text.secondary">No saved lots found</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>
            </CardContent>
          )}
        </Card>
      </Box>
    </Fade>
  );
};

export default UserActivityTracking;